/**
 * Wall-clock time in an IANA time zone, using only the browser's `Intl` data.
 *
 * Two jobs: turn "2026-06-21 12:00 in America/New_York" into the UTC instant it names, and
 * read the zone's date and time back from an instant. The `Intl.DateTimeFormat("en-US")`
 * calls here pull numeric parts out of a formatted date, so they keep a fixed locale on
 * purpose — they are machine plumbing, not text a reader sees.
 */

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = FORMATTERS.get(timeZone);
  if (cached) {
    return cached;
  }
  const made = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  FORMATTERS.set(timeZone, made);
  return made;
}

/** Is this a time zone the browser's `Intl` knows? */
export function isKnownZone(timeZone: string): boolean {
  try {
    formatterFor(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** The wall-clock reading a zone shows at a UTC instant (milliseconds since the epoch). */
export function wallClockAt(ms: number, timeZone: string): WallClock {
  const parts = formatterFor(timeZone).formatToParts(new Date(ms));
  const get = (type: string): number =>
    Number.parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

function wallAsUtcMs(wall: WallClock): number {
  const date = new Date(0);
  date.setUTCFullYear(wall.year, wall.month - 1, wall.day);
  date.setUTCHours(wall.hour, wall.minute, wall.second, 0);
  return date.getTime();
}

/**
 * The UTC instant a wall-clock reading names in a zone.
 *
 * Offset-difference technique: treat the wall time as UTC for a first guess, read that
 * instant back in the zone, and subtract the drift; a second pass settles the case where
 * the guess straddles a daylight-saving change. A time that does not exist (the spring
 * gap) lands an hour later; a repeated time (the autumn overlap) gives the first one.
 */
export function zonedToInstant(wall: WallClock, timeZone: string): number {
  const target = wallAsUtcMs(wall);
  let guess = target;
  for (let pass = 0; pass < 2; pass += 1) {
    guess += target - wallAsUtcMs(wallClockAt(guess, timeZone));
  }
  return guess;
}

/** The zone's offset from UTC at an instant, in hours (east positive). */
export function offsetHoursAt(ms: number, timeZone: string): number {
  const shown = wallAsUtcMs(wallClockAt(ms, timeZone));
  return (shown - Math.floor(ms / 1000) * 1000) / 3_600_000;
}

/** Day number (days since 1970-01-01) of a calendar date. */
export function dayNumber(year: number, month: number, day: number): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(0, 0, 0, 0);
  return Math.round(date.getTime() / 86_400_000);
}

/** The calendar date `days` days after 1970-01-01. */
export function civilFromDayNumber(days: number): {
  year: number;
  month: number;
  day: number;
} {
  const date = new Date(days * 86_400_000);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

const RE_ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

export interface CivilDay {
  year: number;
  month: number;
  day: number;
}

/** Parse `YYYY-MM-DD` (the value of a date input); null when it is not a real date. */
export function parseIsoDay(text: string): CivilDay | null {
  const match = RE_ISO_DATE.exec(text.trim());
  if (match === null) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const back = civilFromDayNumber(dayNumber(year, month, day));
  if (back.year !== year || back.month !== month || back.day !== day) {
    return null;
  }
  return { year, month, day };
}

/** Parse `HH:MM` or `HH:MM:SS` (the value of a time input) to minutes and seconds. */
export function parseClock(
  text: string
): { hour: number; minute: number; second: number } | null {
  const match = RE_TIME.exec(text.trim());
  if (match === null) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = match[3] === undefined ? 0 : Number(match[3]);
  if (hour > 23 || minute > 59 || second > 59) {
    return null;
  }
  return { hour, minute, second };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** `YYYY-MM-DD` for a civil day. */
export function isoDay(day: CivilDay): string {
  return `${String(day.year).padStart(4, "0")}-${pad2(day.month)}-${pad2(day.day)}`;
}

/** `HH:MM` for a wall clock. */
export function clockText(wall: { hour: number; minute: number }): string {
  return `${pad2(wall.hour)}:${pad2(wall.minute)}`;
}

/** The zone's current date and time, for a "now" button. Never call during render. */
export function nowInZone(timeZone: string): {
  date: string;
  time: string;
} {
  const wall = wallClockAt(Date.now(), timeZone);
  return { date: isoDay(wall), time: clockText(wall) };
}

/** The zone's calendar date at an instant, as a day number. */
export function localDayNumber(ms: number, timeZone: string): number {
  const wall = wallClockAt(ms, timeZone);
  return dayNumber(wall.year, wall.month, wall.day);
}

/** "UTC−4" or "UTC+5:30" for an offset in hours. */
export function offsetLabel(hours: number): string {
  if (hours === 0) {
    return "UTC";
  }
  const sign = hours < 0 ? "−" : "+";
  const total = Math.round(Math.abs(hours) * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `UTC${sign}${h}` : `UTC${sign}${h}:${pad2(m)}`;
}
